import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSpecies } from '@agria/paws/src/hooks/useSpecies';
import { ButtonBar } from '@agria/theme/src/components/buttonBar/ButtonBar';
import { Grid } from '@agria/theme/src/components/grid/Grid';
import { TickCircle } from '@agria/theme/src/components/icon';
import { SpeciesSelector } from '@agria/theme/src/components/speciesSelector/SpeciesSelector';
import { Stack } from '@agria/theme/src/components/stack/Stack';
import { useSiteMetadata } from '@agria/theme/src/hooks/useSiteMetadata';
import { useHasMounted } from '@agria/utils/src/hooks/useHasMounted';
import { DevTool } from '@hookform/devtools';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useDataLayerPush } from '@agria/theme/src/hooks/useDataLayerPush';
import { useSiteCountry } from '@agria/theme/src/hooks/useSiteCountry';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Typography } from '@agria/theme/src/components/typography/Typography';
import { Alert } from '@agria/theme/src/components/alert/Alert';
import { useJourneyTracker } from '@agria/theme/src/hooks/useJourneyTracker';
import { siteLinks } from '@agria/theme/src/const/siteLinks';
import { FormError } from '@agria/theme/src/components/form/formError/FormError';
import { useActivationDataLayerPush } from '@agria/theme-qab/src/hooks/useActivationDataLayerPush';
import { useWTODataLayer } from '@agria/theme-qab/src/hooks/useWTODataLayer';
import useContentTypeMapping from '@agria/theme/src/hooks/useContentTypeMapping';
import { ExperimentsContext } from '@agria/theme/src/context/ExperimentsContext';
import britishEquestrianImageSrc from '@agria/theme/src/images/logos/british-equestrian-logo.png';
import { WhichDogSvg } from '@agria/theme/src/components/whichDog/WhichDog';
import { WhichCatSvg } from '@agria/theme/src/components/whichCat/WhichCat';
import useGetDataLayerContentType from '../../hooks/useGetDataLayerContentType';
import { speciesReference } from '../../const/speciesReference';
import { ActiveStepContext } from '../../context/ActiveStepContext';
import { QuoteContext, RequestedQuotes } from '../../context/QuoteContext';
import { useIsLitter } from '../../hooks/useIsLitter';
import { useSpeciesImages } from '../../hooks/useSpeciesImages';
import { FormSteps } from '../../types/global';
import { useIsPetOrAnimal } from '../../hooks/useIsPerOrAnimal';
import { useAffiliateDetails } from '../../hooks/useAffiliateDetails';
import { subSpeciesIds } from '../../const/subSpeciesIds';
import tokens from './tokens';

const stepId: FormSteps = 'species';

export interface SelectedPets {
  species: Record<string, any>;
  subSpecies?: string;
  VetChecked?: boolean;
  VetId?: string;
}

export interface SpeciesSelectionProps {
  agriaId?: string;
  children?: React.ReactNode;
  customSubmit?: (data: RequestedQuotes) => void;
  defaultSpeciesName?: string | null;
  isToggle?: boolean;
  journey?: 'breeder_enrolment' | 'litter_capture' | 'activation' | 'quote';
  nextStep?: FormSteps;
  showDiscount?: boolean;
  type?: 'pet' | 'animal';
  isVetOwnerActivated?: boolean;
}

export interface StyledStackProps {
  desktopColumns: string;
}

// Styles

const StyledStack = styled(Stack)<StyledStackProps>`
  ${({ theme: { space }, desktopColumns }) => css`
    @media (min-width: ${space.large}) {
      ${desktopColumns === '3' &&
      `
        margin: auto;
        max-width: 780px;
      `}
      ${desktopColumns === '2' &&
      `
        margin: auto;
        max-width: ${tokens.selectorPanelMaxWidth};
      `}
    }
  `}
`;

const StyledFormErrorContainer = styled.div`
  display: grid;
  place-items: center;

  > p {
    margin: 0;
  }
`;

const StyledAlertError = styled(Alert)`
  align-items: center;
`;

const StyledBritishEquestrianLogo = styled.div`
  ${({ theme: { colors } }) => css`
    padding: 8px;
    box-shadow: inset 0 0 0 1px ${colors.neutral100};
    border-radius: 8px;
    background-color: ${colors.utilityWhite};
    margin-left: 2px;

    img {
      height: auto;
    }
  `};
`;

const StyledCertificationRow = styled.div`
  ${({ theme: { media, space } }) => css`
    display: flex;
    align-items: center;
    justify-content: center;

    gap: ${space.xxSmall};

    @media (min-width: ${media.small}) {
      display: none;
    }
  `};
`;

export const SpeciesSelection = ({
  showDiscount,
  customSubmit,
  children,
  nextStep = 'petDetails',
  isToggle,
  type = 'pet',
  journey,
  agriaId,
  defaultSpeciesName,
  isVetOwnerActivated,
}: SpeciesSelectionProps) => {
  const rawSpecies = useSpecies();
  const isLitter = useIsLitter();
  const { activeQuote, dispatch, requestedQuotes, dataLayerContentType } =
    useContext(QuoteContext);
  const { setActiveStep } = useContext(ActiveStepContext);
  useActivationDataLayerPush();
  const [formData, setFormData] = useState<string>();
  const {
    isFreeJourney,
    contactNumber,
    subSpecies,
    includeMultiPetDiscountTerms,
  } = useSiteMetadata();
  const { yourThe, petOrAnimal } = useIsPetOrAnimal();
  const { dataLayerPush } = useDataLayerPush();
  const { journeyNumber } = useJourneyTracker();
  const getDataLayerContentType = useGetDataLayerContentType();
  const hasMounted = useHasMounted();
  const { affiliateType } = useAffiliateDetails();
  const { isIe } = useSiteCountry();
  const { getCurrentContentType } = useContentTypeMapping();
  const { setWTData } = useWTODataLayer();

  const { experiments } = useContext(ExperimentsContext);

  if (!isFreeJourney) {
    journey = 'quote';
  }
  const {
    campaign: { affiliateId },
  } = useContext(QuoteContext);

  const species = useMemo(
    () =>
      rawSpecies.map((s) => ({
        ...s,
        litterName: speciesReference[s.id].litterName ?? '',
      })),
    [rawSpecies]
  );

  // Get images
  const images = useSpeciesImages();

  const generatePetInfos = (payload) => {
    const petInfos = {};
    let counter = 1;

    Object.entries(payload).forEach(([speciesId, data]) => {
      if (data.count > 0) {
        for (let i = 0; i < data.count; i++) {
          petInfos[`pet_info_${counter}`] = {
            pet_species:
              speciesReference[speciesId]?.name.toLowerCase() || 'unknown',
          };
          counter += 1;
        }
      }
    });

    return petInfos;
  };

  // 1. Get selected pets from form
  const onSubmit = (data: SelectedPets) => {
    // Set WTO data for each species based on selection
    const validSpecies = ['dog', 'cat', 'horse', 'rabbit'];
    const selectedSpeciesIds = Object.keys(data.species).filter(
      (speciesId) => data.species[speciesId].count > 0
    );

    validSpecies.forEach((species) => {
      const capitalisedSpecies =
        species.charAt(0).toUpperCase() + species.slice(1);

      // Check if this species is selected by looking at the species reference
      const isSpeciesSelected = selectedSpeciesIds.some((speciesId) => {
        const speciesData = speciesReference[speciesId];
        return (
          speciesData?.displayName === species ||
          (species === 'horse' && speciesData?.displayName === 'horse') ||
          (species === 'horse' && speciesData?.name === 'Equine')
        );
      });

      setWTData(`Has Selected ${capitalisedSpecies}`, isSpeciesSelected);
    });

    // 2. Build payload
    const payload: RequestedQuotes = Object.entries(data.species).reduce(
      (prev, [key, value]) => {
        // 3. Check if pet count is greater than 0
        if (value.count <= 0) {
          return prev;
        }

        // 4. Build correct structure
        const existingQuoteIds = requestedQuotes?.[key]?.quoteIds || [];
        return {
          ...prev,
          [key]: {
            count: value.count,
            quoteIds: [...existingQuoteIds],
          },
        };
      },
      {}
    );
    if (customSubmit) {
      customSubmit(payload);
      return;
    }

    // Initialize pet infos for all selected pets
    dispatch({
      type: 'INITIALISE_PET_INFOS',
      payload,
    });

    // set the quote request first

    dispatch({
      type: 'SET_QUOTE_REQUESTS',
      payload,
    });

    // Initialize pet_info_x objects
    // Object.keys(payload).forEach((key, index) => {
    //   const petInfoKey = `pet_info_${index + 1}`;
    //   dispatch({
    //     type: 'SET_PET_INFO',
    //     payload: {
    //       key: petInfoKey,
    //       info: {
    //         pet_species: speciesReference[key]?.name,
    //       },
    //     },
    //   });
    // });

    // if we have a sub species we need to add it to the active pet
    if (subSpecies) {
      dispatch({
        type: 'SET_ACTIVE_PET',
        payload: {
          ...activeQuote?.Pet,
          SubSpeciesId: subSpeciesIds.find((item) => item.name === subSpecies)
            ?.id,
        },
      });
    }

    // then add on any extras
    if (data.VetId || data.VetChecked) {
      dispatch({
        type: 'SET_ACTIVE_PET',
        payload: {
          ...activeQuote?.Pet,
          ...(data.VetId && { VetId: data.VetId }),
          ...(data.VetChecked && { VetChecked: data.VetChecked }),
        },
      });
    }
    // if vet id we also need to make it as an affiliate
    if (data.VetId) {
      dispatch({
        type: 'SET_AFFILIATE_ID',
        payload: data.VetId,
      });
    }
    // Start complete
    const petInfos = generatePetInfos(payload);
    if (isFreeJourney) {
      const { contentType } = getCurrentContentType();
      dataLayerPush({
        content_group: `activation journey`,
        event: `${journey}_journey_interaction`,
        content_type: contentType,
        customer_type: `${affiliateType || 'owner'}`,
        funnel_stage: 'select species',
        funnel_stage_number:
          contentType === 'breeder club - main join' ? 1.5 : 1,
        interaction_type: 'stage completed',
        journey_number: journeyNumber,
        ...(journey !== 'litter_capture' &&
          journey !== 'breeder_enrolment' && {
            selection_number: Object.values(payload).reduce(
              (prev, cur) => prev + cur.count,
              0
            ),
          }),
        ...(journey !== 'breeder_enrolment' && {
          selection_type: getDataLayerContentType({
            requestedQuotes: payload,
            journey,
          }).toLowerCase(),
        }),
        ...(agriaId !== undefined && { agria_id: agriaId }),
        ...petInfos,
      });
    } else {
      dataLayerPush({
        content_group: 'quote and buy journey',
        content_type: 'quote and buy - main',
        event: `quote_journey_interaction`,
        funnel_stage: 'select species',
        funnel_stage_number: 1,
        interaction_type: 'stage completed',
        journey_number: journeyNumber,
        quote_type: 'new quote',
        selection_number: Object.values(payload).reduce(
          (prev, cur) => prev + cur.count,
          0
        ),
        selection_type: getDataLayerContentType({
          requestedQuotes: payload,
          journey: undefined,
          subSpecies,
        }).toLowerCase(),
        ...(agriaId !== undefined && { agria_id: agriaId }),
        ...petInfos,
      });

      dispatch({
        type: 'SET_QUOTE_TYPE',
        payload: 'new quote',
      });
    }

    setActiveStep(nextStep);
  };

  const onError = (errorFields: any) => {
    Object.keys(errorFields).forEach((fieldName) => {
      const error = errorFields[fieldName];
      dataLayerPush({
        event: `${journey}_journey_interaction`,
        interaction_type: `error on: ${fieldName}`,
        error_message: 'Please make a selection',
        funnel_stage: 'select species',
        funnel_stage_number: 1,
      });
    });
  };

  const defaultSpecies = useMemo(() => {
    const start = species.reduce(
      (prev, cur) => ({
        ...prev,
        [cur.id]: {
          quoteIds: [],
          count:
            species.length === 1 ||
            (defaultSpeciesName &&
              cur.name.toLowerCase() === defaultSpeciesName)
              ? 1
              : 0, /// If there is only one species, set the count to 1
        },
      }),
      {}
    );

    if (requestedQuotes) {
      return {
        ...start,
        ...requestedQuotes,
      };
    }

    return start;
  }, [requestedQuotes, species, defaultSpeciesName]);

  const defaultValues = useMemo(
    () => ({
      species: defaultSpecies,
    }),
    [defaultSpecies]
  );

  const formMethods = useForm<SelectedPets>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
  });
  const { control, handleSubmit, reset, formState, watch } = formMethods;

  const activeSpeciesWatch = watch('species');
  useEffect(() => {
    const latest = JSON.stringify(defaultSpecies, null, 0);
    // Update the defaultValues when formData changes
    if (latest !== formData) {
      reset(defaultValues);
      setFormData(latest);
    }
  }, [defaultSpecies, defaultValues, formData, reset]);

  // get the activly select species with a count

  const activeSpeciesName = useMemo(() => {
    const active = Object.entries(activeSpeciesWatch || {}).find(
      ([key, value]) => value.count > 0
    );

    if (active) {
      return species.find((s) => s.id === active[0])?.name;
    }
    return null;
  }, [activeSpeciesWatch, species]);

  const activeSpeciesNameLabel = activeSpeciesName
    ?.toLowerCase()
    .replace('equine', 'horse');

  const activeSpeciesNameLabelLitter = activeSpeciesName
    ?.toLowerCase()
    .replace('dog', 'puppy')
    .replace('cat', 'kitten');

  // Count how many species are selected
  const countSelectedSpecies = useMemo(
    () =>
      Object.values(activeSpeciesWatch || {}).reduce(
        (prev: number, cur) =>
          prev + ((cur.count === '' ? 0 : cur.count) as number),
        0
      ),
    [activeSpeciesWatch]
  );

  // count how many species are selected
  // each species has a count as a property on the object
  // place each oject into an array if the count is greater than 0
  const isMultiSpecies = useMemo(() => {
    const list = Object.values(activeSpeciesWatch || {}).filter(
      (s) => s.count > 0
    );

    return list.length > 1;
  }, [activeSpeciesWatch]);

  const isOnePet = species.length === 1;

  const yourTheOverride =
    journey === 'litter_capture' || journey === 'breeder_enrolment'
      ? 'your'
      : yourThe;
  let buttonLabel =
    isMultiSpecies || countSelectedSpecies === 0
      ? `Tell us about ${yourTheOverride} ${type}${
          countSelectedSpecies > 1 ? 's' : ''
        }`
      : `Tell us about ${yourTheOverride} ${
          isLitter
            ? `${activeSpeciesNameLabelLitter}${
                countSelectedSpecies > 1 ? 's' : ''
              }`
            : `${activeSpeciesNameLabel}${
                countSelectedSpecies > 1 ? 's' : ''
              }` || `${type === 'pet' ? 'pet' : ''}`
        }`;

  if (nextStep === 'ownerDetails' || isIe) {
    buttonLabel = 'Tell us about you';
  }

  const desktopColumns = () => {
    if (isOnePet) return 'none';
    return species.length <= 3 ? `${species.length}` : '4';
  };

  const desktopCols = desktopColumns();

  const discountText = () => {
    if (!isFreeJourney && !isIe && !subSpecies) {
      return showDiscount && countSelectedSpecies > 1 ? (
        <>
          <TickCircle color="brandOne100" /> Your 5% multi-pet discount will be
          applied to your quote
        </>
      ) : (
        'Get a 5% multi-pet discount when you insure more than one pet with us'
      );
    }
    if (isIe) {
      return showDiscount && countSelectedSpecies > 1 ? (
        <>
          <TickCircle /> Your multi-pet discount will be applied to your quote
        </>
      ) : null;
    }

    return null;
  };

  const discountTextTwo = () => {
    if (
      !isFreeJourney &&
      !isIe &&
      !subSpecies &&
      includeMultiPetDiscountTerms
    ) {
      return `<a href="${siteLinks.multiPetTermsAndConditions}" target="_blank">Terms and conditions apply</a>`;
    }

    return null;
  };

  return (
    <>
      {experiments.AGRIA108 === 'variation' &&
        experiments.AGRIA118 !== 'variation' && (
          <StyledCertificationRow>
            {Object.values(species).find(({ name }) => name === 'Dog') && (
              <WhichDogSvg width={122} height={96} />
            )}
            {Object.values(species).find(({ name }) => name === 'Cat') && (
              <WhichCatSvg width={122} height={96} />
            )}
            {Object.values(species).find(({ name }) => name === 'Equine') && (
              <StyledBritishEquestrianLogo>
                <img
                  src={britishEquestrianImageSrc}
                  alt="British Equestrian Official Partner"
                  width={104}
                  height={64}
                />
              </StyledBritishEquestrianLogo>
            )}
          </StyledCertificationRow>
        )}

      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <StyledStack gap={tokens.gap} desktopColumns={desktopCols}>
            <Grid
              mobileColumns={isOnePet ? 'none' : '2'}
              desktopColumns={desktopCols}
              desktopGap={isOnePet ? undefined : 'large'}
            >
              {species.map((s) => {
                let imageName = s.name.toLowerCase();

                if (isLitter) {
                  if (imageName === 'dog') {
                    imageName = 'puppy';
                  }
                  if (imageName === 'cat') {
                    imageName = 'kitten';
                  }
                }

                if (subSpecies) {
                  // lower case and remove spaces
                  imageName = subSpecies.toLowerCase().replace(' ', '');
                }

                return (
                  <Controller
                    key={s.id}
                    name="species"
                    control={control}
                    render={({ field }) => (
                      <SpeciesSelector
                        hideCheckbox={isOnePet}
                        image={images[imageName]}
                        imageRight={isOnePet}
                        isLitter={isLitter}
                        isToggle={isToggle}
                        species={s}
                        subSpecies={subSpecies}
                        {...field}
                      />
                    )}
                    rules={{
                      validate: (v) =>
                        !!Object.entries(v).find(([, obj]) => obj.count > 0),
                      // validate: (value) => value > 0,
                    }}
                  />
                );
              })}
            </Grid>

            {formState.errors.species && (
              <StyledFormErrorContainer>
                {tokens.speciesSelectionErrorMessage ? (
                  <FormError display message="Please make a selection" />
                ) : (
                  <StyledAlertError
                    text="Please make a selection"
                    variant="warning"
                  />
                )}
              </StyledFormErrorContainer>
            )}

            {children}

            {experiments.AGRIA108 === 'variation' ||
            experiments.AGRIA118 === 'variation' ? (
              <ButtonBar
                additionalTextPre={discountText()}
                buttonOne={{
                  'aria-label': `Next: ${buttonLabel.replace(
                    'puppys',
                    'puppies'
                  )}`,
                  id: 'species-selection-next',
                  label: buttonLabel.replace('puppys', 'puppies'),
                  type: 'submit',
                }}
                centered
              />
            ) : (
              <ButtonBar
                additionalText={discountText()}
                additionalTextTwo={discountTextTwo()}
                buttonOne={{
                  'aria-label': `Next: ${buttonLabel.replace(
                    'puppys',
                    'puppies'
                  )}`,
                  id: 'species-selection-next',
                  label: buttonLabel.replace('puppys', 'puppies'),
                  type: 'submit',
                }}
                centered
              />
            )}

            {subSpecies && subSpecies === 'service dog' && (
              <Stack gap="xxxSmall">
                <Typography centered variant="headingSmall">
                  Do you have three or more service dogs to insure?
                </Typography>
                <Typography centered variant="bodyMedium">
                  Give us a call on {contactNumber} and we will assist you with
                  insuring all of your service dogs today.
                </Typography>
              </Stack>
            )}
          </StyledStack>

          <DevTool control={control} />
        </form>
      </FormProvider>
    </>
  );
};
